// FanSettings — Feb 2026 (Randy pt89c). Fan-focused settings page (analog
// to /dashboard/account-settings but stripped to what a non-creator actually
// needs). Reached from the IdentityPill "Fan Settings" row (fan users only)
// and from the FanPortal footer.
//
// What lives here:
//   1. Name (editable — PATCH /me/account)
//   2. Email (editable, requires current password — same endpoint)
//   3. Change password — POST /me/change-password
//   4. Billing / payment methods → Stripe Customer Portal
//      (only if the fan already has a stripe_customer_id; otherwise a
//      friendly "You'll set this up when you subscribe to your first
//      paid silo" placeholder).
//   5. Delete account nuke button (link to existing termination flow).
//
// Not creator-Stripe-Connect, not silo pricing, not LLM keys — this page
// is for people who ONLY consume silos.
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UserCircle, EnvelopeSimple, Lock, CreditCard, ArrowLeft, Warning, Spinner, ArrowSquareOut } from "@phosphor-icons/react";
import { toast } from "sonner";
import { api, getUser, setUser, clearAuth } from "@/lib/api";

export default function FanSettings() {
  const nav = useNavigate();
  const initial = getUser() || {};
  const [name, setName] = useState(initial.name || "");
  const [email, setEmail] = useState(initial.email || "");
  const [currentPassword, setCurrentPassword] = useState("");
  const [nameBusy, setNameBusy] = useState(false);
  const [emailBusy, setEmailBusy] = useState(false);
  // Change password
  const [pwCurrent, setPwCurrent] = useState("");
  const [pwNew, setPwNew] = useState("");
  const [pwConfirm, setPwConfirm] = useState("");
  const [pwBusy, setPwBusy] = useState(false);
  // Billing
  const [hasBilling, setHasBilling] = useState(!!initial.stripe_customer_id);
  const [billingBusy, setBillingBusy] = useState(false);

  useEffect(() => {
    // Refresh from the server so the identity is authoritative.
    api.get("/auth/me").then((r) => {
      const u = r.data;
      if (u?.id) {
        setName(u.name || "");
        setEmail(u.email || "");
        setHasBilling(!!u.stripe_customer_id);
      }
    }).catch(() => {});
  }, []);

  const saveName = async () => {
    const clean = (name || "").trim();
    if (!clean) { toast.error("Name can't be empty."); return; }
    if (clean === (initial.name || "")) { toast("No change to save."); return; }
    setNameBusy(true);
    try {
      const r = await api.patch("/me/account", { name: clean });
      if (r.data?.user) setUser(r.data.user);
      toast.success("Name saved.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setNameBusy(false); }
  };

  const saveEmail = async () => {
    const clean = (email || "").trim().toLowerCase();
    if (!clean) { toast.error("Email can't be empty."); return; }
    if (clean === (initial.email || "").toLowerCase()) { toast("No change to save."); return; }
    if (!currentPassword) { toast.error("Enter your current password to change your email."); return; }
    setEmailBusy(true);
    try {
      const r = await api.patch("/me/account", { email: clean, current_password: currentPassword });
      if (r.data?.user) setUser(r.data.user);
      setCurrentPassword("");
      toast.success("Email saved. You may need to sign in again.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't save."); }
    finally { setEmailBusy(false); }
  };

  const changePassword = async () => {
    if (!pwCurrent) { toast.error("Enter your current password."); return; }
    if (!pwNew || pwNew.length < 8) { toast.error("New password must be at least 8 characters."); return; }
    if (pwNew !== pwConfirm) { toast.error("New password and confirmation don't match."); return; }
    setPwBusy(true);
    try {
      await api.post("/me/change-password", { current_password: pwCurrent, new_password: pwNew });
      setPwCurrent(""); setPwNew(""); setPwConfirm("");
      toast.success("Password changed.");
    } catch (e) { toast.error(e.response?.data?.detail || "Couldn't change password."); }
    finally { setPwBusy(false); }
  };

  const openBilling = async () => {
    setBillingBusy(true);
    try {
      const r = await api.post("/stripe/billing/portal", { origin_url: window.location.origin });
      const url = r.data?.url;
      if (url) window.location.href = url;
      else toast.error("Couldn't open the billing portal.");
    } catch (e) {
      if (e.response?.status === 412) {
        toast("No billing yet — you'll set this up when you subscribe to a paid silo.", { icon: "💳" });
      } else {
        toast.error(e.response?.data?.detail || "Couldn't open billing.");
      }
    } finally { setBillingBusy(false); }
  };

  return (
    <div className="min-h-screen bg-[#FDFCF8]">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <button onClick={() => nav(-1)} data-testid="fan-settings-back"
          className="inline-flex items-center gap-1.5 text-sm text-[#6B6A68] hover:text-[#1C1B1A] mb-4">
          <ArrowLeft size={14} /> Back
        </button>
        <h1 className="font-serif-display text-3xl sm:text-4xl text-[#1C1B1A]" data-testid="fan-settings-title">Fan Settings</h1>
        <p className="text-sm text-[#6B6A68] mt-2 leading-relaxed">
          Manage your name, email, password, and billing. If a family member invited you to their household, your family view is on the <a href="/fan-portal" className="underline hover:text-[#1C1B1A]">Fan Portal</a>.
        </p>

        {/* Name */}
        <section className="mt-8 bg-white border hairline rounded-xl p-5" data-testid="fan-settings-name-card">
          <div className="flex items-center gap-2 mb-2">
            <UserCircle size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Your name</h2>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3">This is how family and creators see you across My Silo.</p>
          <div className="flex items-center gap-2 flex-wrap">
            <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
              data-testid="fan-settings-name-input"
              className="flex-1 min-w-[220px] px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            <button onClick={saveName} disabled={nameBusy}
              data-testid="fan-settings-name-save"
              className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
              {nameBusy ? "Saving…" : "Save name"}
            </button>
          </div>
        </section>

        {/* Email */}
        <section className="mt-4 bg-white border hairline rounded-xl p-5" data-testid="fan-settings-email-card">
          <div className="flex items-center gap-2 mb-2">
            <EnvelopeSimple size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Your email</h2>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3">Login identifier + where receipts &amp; family invites arrive. Changing requires your current password.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
            <label className="text-xs text-[#6B6A68]">
              <span>New email</span>
              <input value={email} type="email" onChange={(e) => setEmail(e.target.value)}
                data-testid="fan-settings-email-input"
                className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            </label>
            <label className="text-xs text-[#6B6A68]">
              <span>Current password (to confirm)</span>
              <input value={currentPassword} type="password" onChange={(e) => setCurrentPassword(e.target.value)}
                data-testid="fan-settings-email-current-pw"
                className="mt-1 w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            </label>
          </div>
          <div className="mt-3 flex justify-end">
            <button onClick={saveEmail} disabled={emailBusy}
              data-testid="fan-settings-email-save"
              className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50">
              {emailBusy ? "Saving…" : "Save email"}
            </button>
          </div>
        </section>

        {/* Password */}
        <section className="mt-4 bg-white border hairline rounded-xl p-5" data-testid="fan-settings-password-card">
          <div className="flex items-center gap-2 mb-2">
            <Lock size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Change password</h2>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3">Minimum 8 characters. Existing sessions on other devices stay logged in until they expire.</p>
          <div className="space-y-2">
            <input value={pwCurrent} type="password" placeholder="Current password"
              onChange={(e) => setPwCurrent(e.target.value)} data-testid="fan-settings-pw-current"
              className="w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            <input value={pwNew} type="password" placeholder="New password"
              onChange={(e) => setPwNew(e.target.value)} data-testid="fan-settings-pw-new"
              className="w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            <input value={pwConfirm} type="password" placeholder="Confirm new password"
              onChange={(e) => setPwConfirm(e.target.value)} data-testid="fan-settings-pw-confirm"
              className="w-full px-3 py-2 border hairline rounded-md text-sm bg-[#FDFCF8] focus:outline-none focus:border-[#4A5D4E]" />
            <div className="flex justify-end">
              <button onClick={changePassword} disabled={pwBusy}
                data-testid="fan-settings-pw-save"
                className="text-sm px-4 py-2 rounded-md bg-[#1C1B1A] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
                {pwBusy && <Spinner size={12} className="animate-spin" />} Change password
              </button>
            </div>
          </div>
        </section>

        {/* Billing */}
        <section className="mt-4 bg-white border hairline rounded-xl p-5" data-testid="fan-settings-billing-card">
          <div className="flex items-center gap-2 mb-2">
            <CreditCard size={18} weight="duotone" className="text-[#4A5D4E]" />
            <h2 className="font-serif-display text-lg text-[#1C1B1A]">Billing &amp; payment methods</h2>
          </div>
          {hasBilling ? (
            <>
              <p className="text-xs text-[#6B6A68] mb-3">
                Opens your Stripe billing portal — manage cards, see receipts, cancel any silo subscription. One portal covers everything you pay for on My Silo.
              </p>
              <button onClick={openBilling} disabled={billingBusy}
                data-testid="fan-settings-billing-open"
                className="text-sm px-4 py-2 rounded-md bg-[#4A5D4E] text-[#FDFCF8] hover:bg-[#3A4A3E] disabled:opacity-50 flex items-center gap-1.5">
                {billingBusy ? <Spinner size={12} className="animate-spin" /> : <ArrowSquareOut size={13} />} Open billing portal
              </button>
            </>
          ) : (
            <div className="text-xs text-[#6B6A68] p-3 bg-[#FBF7EE] border border-[#EAD9AE] rounded-md leading-relaxed">
              You don&apos;t have any billing set up yet. When you subscribe to a paid silo, Stripe will collect your card details and you&apos;ll be able to manage everything from here. Free silos and family invites don&apos;t require billing.
            </div>
          )}
        </section>

        {/* Delete account */}
        <section className="mt-4 bg-white border-2 border-[#EACAB8] rounded-xl p-5" data-testid="fan-settings-danger-card">
          <div className="flex items-center gap-2 mb-2">
            <Warning size={18} weight="duotone" className="text-[#C16E5A]" />
            <h2 className="font-serif-display text-lg text-[#A55A48]">Delete my account</h2>
          </div>
          <p className="text-xs text-[#6B6A68] mb-3">
            Permanently deletes your account, all subscriptions, and any family memberships. See our <a href="/policy/account-termination" className="underline hover:text-[#1C1B1A]">termination policy</a> first.
          </p>
          <button onClick={() => nav("/policy/account-termination")}
            data-testid="fan-settings-delete-open"
            className="text-sm px-4 py-2 rounded-md border border-[#C16E5A] text-[#A55A48] bg-white hover:bg-[#FBEBE4]">
            Review termination policy
          </button>
        </section>

        <section className="mt-6 text-center">
          <button onClick={() => { clearAuth(); nav("/login"); }}
            data-testid="fan-settings-logout"
            className="text-xs text-[#6B6A68] hover:text-[#C16E5A] underline">
            Sign out
          </button>
        </section>
      </div>
    </div>
  );
}
